"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useMemo } from "react";
import Turnstile from "react-turnstile";

import { Register } from "@/features/auth/api/login";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler, { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CardDescription } from "@/components/ui/card";
import envs from "@/envs.ts";
import { getLanguageCode } from "@/i18n/getDateFnsLocale.ts";

interface RegisterFormProps {
  goToNextStep: () => void;
}

// Email regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function RegisterForm({ goToNextStep }: RegisterFormProps) {
  const { t } = useTranslation(["forms", "common", "auth"]);
  const { setEmail } = useAuthStore();
  const createRegisterFormSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t("forms:errors.firstNameReq")),
        lastName: z.string().min(1, t("forms:errors.lastNameReq")),
        email: z
          .string()
          .min(1, t("forms:errors.invalidEmail"))
          .email(t("forms:errors.invalidEmail"))
          .regex(EMAIL_REGEX, t("forms:errors.invalidEmail"))
          .trim(),
        token: z.string({ required_error: t("forms:errors.captcha") }),
      }),
    [i18n.language],
  );

  type RegisterFormValues = z.infer<typeof createRegisterFormSchema>;

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(createRegisterFormSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    control,
  } = form;

  const onSubmit: SubmitHandler<RegisterFormValues> = async ({
    email,
    firstName,
    lastName,
    token,
  }) => {
    if (token) {
      try {
        await Register(email, firstName, lastName);

        setEmail(email);
        goToNextStep();
      } catch (error) {
        const apiErrors = errorHandler<"email">(error);

        assignApiErrorsToFormFields(apiErrors, setError, { email: "email" });
      }
    }
  };

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1, y: 0 }}
        className="grid gap-6 md:p-10 md:pt-4 lg:px-16 xl:px-20"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <CardDescription className="text-muted-foreground m-auto line-clamp-2 block max-w-80 text-center md:w-3/4">
          {t("auth:signUp.createAccount")}
        </CardDescription>
        <motion.div
          animate={{ opacity: 1 }}
          className="grid gap-4"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <motion.div className="relative" whileTap={{ scale: 0.98 }}>
                      <span className="iconify tabler--user absolute left-2 h-full" />
                      <Input
                        className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 py-0 pl-8 text-sm transition-all duration-200 md:py-6 ${
                          errors.firstName
                            ? "border-destructive focus-visible:border-secondary focus-visible:ring-destructive"
                            : ""
                        }`}
                        disabled={isSubmitting}
                        placeholder={t("forms:labels.firstName")}
                        {...field}
                      />
                    </motion.div>
                  </FormControl>
                  <AnimatePresence>
                    {errors.firstName && (
                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        initial={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FormMessage>{errors.firstName.message}</FormMessage>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <motion.div className="relative" whileTap={{ scale: 0.98 }}>
                      <span className="iconify tabler--user absolute left-2 h-full" />
                      <Input
                        className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 py-0 pl-8 text-sm transition-all duration-200 md:py-6 ${
                          errors.lastName
                            ? "border-destructive focus-visible:border-secondary focus-visible:ring-destructive"
                            : ""
                        }`}
                        disabled={isSubmitting}
                        placeholder={t("forms:labels.lastName")}
                        {...field}
                      />
                    </motion.div>
                  </FormControl>
                  <AnimatePresence>
                    {errors.lastName && (
                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        initial={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FormMessage>{errors.lastName.message}</FormMessage>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <motion.div className="relative" whileTap={{ scale: 0.98 }}>
                    <span className="iconify tabler--mail-filled absolute left-2 h-full" />
                    <Input
                      className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 py-0 pl-8 text-sm transition-all duration-200 md:py-6 ${
                        errors.email
                          ? "border-destructive focus-visible:border-secondary focus-visible:ring-destructive"
                          : ""
                      }`}
                      disabled={isSubmitting}
                      placeholder={t("forms:labels.yourEmail")}
                      type="email"
                      {...field}
                    />
                  </motion.div>
                </FormControl>
                <AnimatePresence>
                  {errors.email && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FormMessage>{errors.email.message}</FormMessage>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="token"
            render={({ field }) => (
              <div
                className={
                  "bg-muted/20 border-border/50 relative ms-auto flex w-full items-center justify-center overflow-hidden rounded-md border"
                }
              >
                <Turnstile
                  className={"bg-muted relative box-border w-full overflow-hidden rounded-lg"}
                  fixedSize={true}
                  language={getLanguageCode(i18n.language)}
                  sitekey={envs.VITE_CAPTCHA_KEY}
                  size={"flexible"}
                  onVerify={(token) => field.onChange(token)}
                />
                <div className={"bg-muted absolute -top-0.5 left-0 z-50 h-1 w-full"} />
                <div className={"bg-muted absolute -bottom-0.5 left-0 z-50 h-1 w-full"} />
                <div className={"bg-muted absolute top-0 -left-0.5 z-50 h-full w-1"} />
                <div className={"bg-muted absolute top-0 -right-0.5 z-50 h-full w-1"} />
              </div>
            )}
          />
        </motion.div>
        <motion.div
          animate={{ opacity: 1 }}
          className="grid gap-4"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background font-medium shadow-xl"
            color="primary"
            disabled={isSubmitting || !form.formState.isValid}
            size="lg"
            type="submit"
          >
            {isSubmitting ? (
              <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
                {t("common:submitting")}
              </motion.div>
            ) : (
              <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
                {t("common:continue")}
              </motion.div>
            )}
          </Button>
        </motion.div>
      </motion.form>
    </Form>
  );
}
